import { type DetailedHTMLProps, type HTMLAttributes, useEffect, useRef, useState } from "react";
import { useLocation } from "react-router";
import { useAuth } from "../auth/AuthContext";
import { inApp } from "../lib/inApp";
import { type ThemeChoice, useTheme } from "../theme/ThemeProvider";
import { BrandGlyph } from "./BrandMark";
import { Sheet } from "./phone/Sheet";
import { installPrompt, onInstalled } from "./phone/shell";
import { isStandalone } from "./phone/usePhone";

type CustomElementProps<T> = DetailedHTMLProps<HTMLAttributes<HTMLElement>, HTMLElement> & T;

declare module "react" {
  namespace JSX {
    interface IntrinsicElements {
      "flashsuite-menu": CustomElementProps<{ app: string; business: string; place: string; compact?: string }>;
      "flashsuite-bar": CustomElementProps<{ app: string }>;
    }
  }
}

interface DeviceItem {
  label: string;
  value?: string;
  onSelect: () => void;
}

interface MenuElement extends HTMLElement {
  setDeviceItems(items: DeviceItem[]): void;
}

/**
 * Load the Flashsuite menu's script once (Flashsuite INTEGRATION.md section 16): it defines
 * `<flashsuite-menu>` and `<flashsuite-bar>`, which talk to Flashsuite themselves.
 */
export function useFlashsuiteScript(): void {
  const { flashsuite } = useAuth();
  const url = flashsuite?.url;
  useEffect(() => {
    if (!url) return;
    const src = `${url}/menu/v1.js`;
    if (document.querySelector(`script[src="${src}"]`)) return;
    const script = document.createElement("script");
    script.src = src;
    script.defer = true;
    document.head.appendChild(script);
  }, [url]);
}

/**
 * Flashsuite's pages for the business's plan (INTEGRATION.md section 17): Billing, and the Start page
 * for a business with no Flashroom plan yet. Null where this deployment has no Flashsuite.
 */
export function flashsuitePages(fs: { url: string; app: string } | null): { billing: string; start: string } | null {
  return fs ? { billing: `${fs.url}/billing`, start: `${fs.url}/start/${fs.app}` } : null;
}

/**
 * The bar across the top of the app; Flashsuite shows it only when the business's payment failed. Not
 * inside the phone app, where it would ask for the card (flashsuite-phone docs/APP-MODE.md section 2).
 */
export function FlashsuiteBar() {
  const { flashsuite } = useAuth();
  return flashsuite && !inApp() ? <flashsuite-bar app={flashsuite.app} /> : null;
}

/**
 * The short name of the screen a path opens, which the menu tells Flashsuite as where the person is
 * (Flashsuite INTEGRATION.md section 16).
 */
export function placeOf(path: string): string {
  const [first = "", id, more, sub] = path.split("/").filter(Boolean);
  switch (first) {
    case "":
      return "Home";
    case "rooms":
      if (!id) return "Rooms";
      if (id === "new") return "New room";
      if (more === "walk") return "Walk it through";
      if (more === "studio") return "Live studio";
      if (more === "sessions" && sub) return "Session report";
      return "Room";
    case "assets":
      return "Assets";
    case "people":
      return "People";
    case "automations":
      return "Automations";
    case "conversations":
      return "Conversations";
    case "settings":
      return "Settings";
    case "more":
      return "More";
    default:
      return "Flashroom";
  }
}

const SHARE = "M12 3v12M8 7l4-4 4 4M5 12v7a2 2 0 0 0 2 2h10a2 2 0 0 0 2-2v-7";

function isIos(): boolean {
  return /iPhone|iPad|iPod/.test(navigator.userAgent) || (navigator.platform === "MacIntel" && navigator.maxTouchPoints > 1);
}

/**
 * The Flashsuite menu, where the workspace menu and the account menu were: the business, the person,
 * switching business, Your apps, Your account, Show & tell, Appearance, this device's items and Log out.
 * This side follows its appearance, signs out on its Log out, names the screen the person is on, and
 * offers Add to Home Screen on this device. The menu keeps the page's last few minutes for Show & tell;
 * parts that change every frame (a playhead, the live stage) or must never be seen (a key) carry the
 * class `fs-norecord`, which it leaves out.
 */
export function FlashsuiteMenu({ compact = false }: { compact?: boolean }) {
  const { user, flashsuite, logout } = useAuth();
  const { pathname } = useLocation();
  const { setChoice } = useTheme();
  const menu = useRef<MenuElement>(null);
  // Why the phone app counts as installed: it is the app on the phone already, so it never offers itself.
  const [installed, setInstalled] = useState(() => isStandalone() || inApp());
  const [installing, setInstalling] = useState(false);

  useEffect(() => {
    const el = menu.current;
    if (!el) return;
    const onAppearance = (e: Event) => setChoice((e as CustomEvent<{ appearance: ThemeChoice }>).detail.appearance);
    const onLogout = () => void logout();
    el.addEventListener("fs-appearance", onAppearance);
    el.addEventListener("fs-logout", onLogout);
    return () => {
      el.removeEventListener("fs-appearance", onAppearance);
      el.removeEventListener("fs-logout", onLogout);
    };
  }, [flashsuite, setChoice, logout]);

  useEffect(() => {
    const el = menu.current;
    if (!el) return;
    let alive = true;
    void customElements.whenDefined("flashsuite-menu").then(() => {
      if (alive) el.setDeviceItems(installed ? [] : [{ label: "Add to Home Screen", onSelect: () => setInstalling(true) }]);
    });
    return () => {
      alive = false;
    };
  }, [flashsuite, installed]);

  if (!flashsuite) return null;

  const install = async () => {
    const prompt = installPrompt();
    if (!prompt) return;
    await prompt.prompt();
    const { outcome } = await prompt.userChoice;
    if (outcome === "accepted") {
      onInstalled();
      setInstalled(true);
      setInstalling(false);
    }
  };

  return (
    <>
      <flashsuite-menu ref={menu} app={flashsuite.app} business={user?.business_id ?? ""} place={placeOf(pathname)} compact={compact ? "" : undefined} />
      <Sheet open={installing} onClose={() => setInstalling(false)} title="Add to Home Screen">
        <div className="ph-a2hs">
          <BrandGlyph size={64} />
          {installPrompt() ? (
            <button type="button" className="ph-btn pri wide" onClick={() => void install()}>
              Add Flashroom
            </button>
          ) : isIos() ? (
            <ol>
              <li>
                In Safari, tap <b>Share</b>{" "}
                <span className="ph-inl">
                  <svg viewBox="0 0 24 24" aria-hidden="true">
                    <path d={SHARE} />
                  </svg>
                </span>
                . On newer iPhones it is inside the <b>⋯</b> button.
              </li>
              <li>
                Tap <b>Add to Home Screen</b>.
              </li>
              <li>
                Tap <b>Add</b>.
              </li>
            </ol>
          ) : (
            <ol>
              <li>
                In Chrome, tap <b>⋮</b> at the top right.
              </li>
              <li>
                Tap <b>Add to Home screen</b>, then <b>Install</b>.
              </li>
            </ol>
          )}
          <p>Flashroom then opens full screen from its own icon, like any app.</p>
        </div>
      </Sheet>
    </>
  );
}
